import { translate } from "@/i18n/i18n.config";
import { flash } from "@/utils/flash";
import * as Sentry from "@sentry/vue";
import type { RouteLocationNormalized, Router } from "vue-router";

// After a deploy the previous build's chunks are gone, so a tab opened before
// it fails as soon as it lazily loads a route it has not visited yet. Vite
// reports that as `vite:preloadError`
// (https://vite.dev/guide/build#load-error-handling). Only a full page load
// recovers: the browser remembers a failed module URL for the life of the
// document.
//
// That load has to go where the user was *going*. vue-router loads lazy route
// components before it commits a navigation, so `location.href` is still the
// page being left (#13673).

const STORAGE_KEY = "chunkReloadAttempts";
const MAX_RELOADS_PER_TARGET = 2;
// Long enough to outlast a slow boot of the reloaded page, so a chunk that
// stays missing cannot turn into a reload loop.
const ATTEMPT_WINDOW_MS = 5 * 60 * 1000;
// One click fires several events (one per failing lazy route record and CSS
// file), all served by the same reload. Short, so links work again if the user
// cancels the unload in a `beforeunload` prompt.
const RELOAD_LATCH_MS = 5000;

// When each load of a target started, so each one expires on its own.
type Attempts = Record<string, number[]>;

interface Decision {
  to: RouteLocationNormalized | undefined;
  at: number;
}

// Vite's own message, the same in every browser.
const isCssError = (event: VitePreloadErrorEvent): boolean =>
  event.payload?.message?.startsWith("Unable to preload CSS") ?? false;

// The attempts of the last ATTEMPT_WINDOW_MS. Throws without storage.
function readAttempts(now: number): Attempts {
  const stored = JSON.parse(
    sessionStorage.getItem(STORAGE_KEY) ?? "{}"
  ) as Partial<Attempts>;
  const attempts: Attempts = {};
  for (const [target, times] of Object.entries(stored)) {
    const recent = (Array.isArray(times) ? times : []).filter(
      (at) => now - at < ATTEMPT_WINDOW_MS
    );
    if (recent.length) attempts[target] = recent;
  }
  return attempts;
}

// Counts full page loads per target across loads of this tab, so a chunk that
// is missing for good stops after MAX_RELOADS_PER_TARGET.
function claimReload(target: string, now: number): boolean {
  try {
    const attempts = readAttempts(now);
    const times = attempts[target] ?? [];
    if (times.length >= MAX_RELOADS_PER_TARGET) return false;

    attempts[target] = [...times, now];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
    return true;
  } catch {
    // Without storage the loads cannot be counted, and an uncounted reload
    // could loop.
    return false;
  }
}

// Takes back the attempt claimed at `at` for a load that never happened.
function refundReload(target: string, at: number): void {
  try {
    const attempts = readAttempts(Date.now());
    const times = attempts[target] ?? [];
    const index = times.indexOf(at);
    if (index === -1) return;
    times.splice(index, 1);
    if (!times.length) delete attempts[target];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
  } catch {
    // Without storage nothing was counted.
  }
}

function hardLoad(target: string): void {
  const url = new URL(target);
  const current = new URL(location.href);
  // `assign` to the current URL (or one that differs only by its #hash)
  // scrolls within the document instead of loading it.
  if (
    url.origin === current.origin &&
    url.pathname === current.pathname &&
    url.search === current.search
  ) {
    location.reload();
  } else {
    location.assign(target);
  }
}

export function installPreloadErrorHandler(router: Router): () => void {
  // vue-router keeps its pending location private, and `beforeResolve` runs
  // after the lazy components are loaded, so track it here. Install this
  // before any other guard, so it holds the final target after redirects.
  let pending: RouteLocationNormalized | undefined;
  // What this page last decided for a failed navigation: load `target`, or
  // give up. One click fails several files (layout, page, stylesheets), and
  // the page's chunk can fail after the layout already ended the navigation,
  // when nothing is pending any more; those events follow the decision. A
  // newer navigation is a new click and gets its own, even within the latch
  // (e.g. after the user stayed in a beforeunload prompt).
  let scheduled: (Decision & { target: string }) | undefined;
  let gaveUp: Decision | undefined;
  const belongsTo = (decision: Decision | undefined, now: number): boolean =>
    decision !== undefined &&
    now - decision.at < RELOAD_LATCH_MS &&
    (pending === undefined || pending === decision.to);

  // Clear only for the navigation being tracked: a superseded navigation still
  // reaches afterEach (cancelled) or onError while the newer one is loading.
  const removeBeforeEach = router.beforeEach((to) => {
    pending = to;
  });
  const removeAfterEach = router.afterEach((to) => {
    if (to === pending) pending = undefined;
  });
  const removeOnError = router.onError((_error, to) => {
    if (to === pending) pending = undefined;
  });

  // The file is still missing after fresh page loads, so this is no stale tab
  // any more. Say so instead of dropping the click.
  const giveUp = (
    event: VitePreloadErrorEvent,
    target: string,
    now: number
  ): void => {
    // No load is coming, so let what did arrive be used: a page without some
    // of its styles beats one that does not open. A missing chunk still fails
    // its navigation, just without orphaned rejections (see below).
    event.preventDefault();
    gaveUp = { to: pending, at: now };

    flash("alert", translate("errors.pageLoadFailed.title"), {
      description: translate("errors.pageLoadFailed.description"),
      autohide: false,
      group: "page-load-failed",
    });
    // Not a message `beforeSend` drops as stale-chunk noise (sentry.ts).
    Sentry.captureMessage("Lazy chunk still missing after reload", {
      level: "warning",
      fingerprint: ["lazy-chunk-still-missing-after-reload"],
      tags: { route: String(pending?.name ?? "") },
      extra: { path: new URL(target).pathname, error: event.payload?.message },
    });
  };

  // The page is about to be replaced. A prevented chunk error resolves the
  // import to `undefined`, which fails the navigation all the same and leaves
  // no unhandled rejection from the imports vue-router stops awaiting. A
  // prevented CSS error, though, lets the chunk load and the route commit
  // under the pending load.
  const failUntilLoad = (event: VitePreloadErrorEvent): void => {
    if (!isCssError(event)) event.preventDefault();
  };

  const onPreloadError = (event: VitePreloadErrorEvent): void => {
    const now = Date.now();
    if (belongsTo(scheduled, now)) return failUntilLoad(event);
    if (belongsTo(gaveUp, now)) return event.preventDefault();

    // This page outlived the load it scheduled (the user stayed, e.g. in a
    // beforeunload prompt), so that load never happened: it doesn't count. A
    // load that did happen started a new page, so the limit still holds.
    if (scheduled) {
      refundReload(scheduled.target, scheduled.at);
      scheduled = undefined;
    }

    // Without a pending navigation the import came from the page itself
    // (e.g. a lazily loaded dialog body), so reload that page.
    const target = new URL(
      pending ? router.resolve(pending).href : location.href,
      location.href
    ).href;
    if (!claimReload(target, now)) return giveUp(event, target, now);

    scheduled = { to: pending, at: now, target };
    // A macrotask, because on back/forward vue-router rewinds the URL with
    // `history.go()` after the error, which cancels a load started earlier.
    setTimeout(() => hardLoad(target), 0);
    failUntilLoad(event);
  };

  window.addEventListener("vite:preloadError", onPreloadError);

  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    removeBeforeEach();
    removeAfterEach();
    removeOnError();
  };
}
